import {
    assigneeFilterLabels,
    migrateAssigneeFilterValues,
    type AssigneeFilterPerson
} from '../../../utils/assigneeFilter.ts';
import { plural } from '../../../utils/plural.ts';
import type { DmInboxStatus } from '../../../services/dmNotifications';
import { tagsToVersions } from './inboxTags.ts';
import { CLOSED_WINDOW, resolvedPatientsLabel } from './monitoringCounts.ts';
import type { ListSortOrder } from './listUrlState';

export interface ListFilterLine {
    key: string;
    label: string;
    value: string;
}

export interface ListFilterSummary {
    leadText: string;
    filters: ListFilterLine[];
    sortText: string;
}

// Column names as the list headers read, and each order in words matching the backend's ORDER BY.
export interface SortPhrase {
    column: string;
    asc: string;
    desc: string;
}

export interface AssigneeLineSource {
    values: readonly string[];
    people: readonly AssigneeFilterPerson[];
    currentUserEmail: string | undefined;
}

export interface PracticeName {
    id: string;
    name: string;
}

export interface PracticeLineSource {
    ids: readonly string[];
    practices: readonly PracticeName[];
    loaded: boolean;
}

export interface SharedFilterSources {
    search: string;
    assignees: AssigneeLineSource;
    practices: PracticeLineSource;
}

const VALUE_SEPARATOR = ', ';

function line(key: string, label: string, values: readonly string[]): ListFilterLine[] {
    return values.length === 0 ? [] : [{ key, label, value: values.join(VALUE_SEPARATOR) }];
}

export function listLeadText(
    total: number | null,
    filtered: boolean,
    status: DmInboxStatus
): string {
    if (status === 'closed') {
        return total === null ? `Patients ${CLOSED_WINDOW}` : resolvedPatientsLabel(total);
    }
    if (filtered)
        return total === null ? 'Filtered list' : `Filtered list · ${plural(total, 'patient')}`;
    return total === null ? 'All patients' : `All patients · ${total.toLocaleString()}`;
}

// The names of the people the API filters by, legacy URL labels included.
function appliedAssigneeLabels({ values, people, currentUserEmail }: AssigneeLineSource): string[] {
    return assigneeFilterLabels(
        migrateAssigneeFilterValues(values, people),
        people,
        currentUserEmail
    );
}

export function assigneeLine(source: AssigneeLineSource): ListFilterLine[] {
    return line('assignee', 'Assignee', appliedAssigneeLabels(source));
}

export interface PracticeNamesStatus {
    isLoading: boolean;
    isError: boolean;
}

// A failed load leaves no names to resolve against, so it reads like one still loading.
export function practiceNamesLoaded({ isLoading, isError }: PracticeNamesStatus): boolean {
    return !isLoading && !isError;
}

// Without loaded names the line counts ids; once loaded, ids that resolve to no name are
// still counted, since the API filters by them too.
export function practiceLine({ ids, practices, loaded }: PracticeLineSource): ListFilterLine[] {
    if (ids.length === 0) return [];
    if (!loaded) return line('practice', 'Practice', [plural(ids.length, 'practice')]);
    const nameById = new Map(practices.map((practice) => [practice.id, practice.name] as const));
    const names = ids.flatMap((id) => nameById.get(id) ?? []);
    const unknownCount = ids.length - names.length;
    const unknown = unknownCount > 0 ? [plural(unknownCount, 'unknown practice')] : [];
    return line('practice', 'Practice', [...names, ...unknown]);
}

export function searchLine(search: string): ListFilterLine[] {
    const trimmed = search.trim();
    return line('search', 'Search', trimmed ? [`“${trimmed}”`] : []);
}

export function tagsLine(tags: readonly string[]): ListFilterLine[] {
    return line('tags', 'Tags', tagsToVersions(tags));
}

export function sortText(phrase: SortPhrase, order: ListSortOrder): string {
    return `Sorted by ${phrase.column}, ${phrase[order]}`;
}

// The API filters by every practice id it gets, resolvable or not, so ids alone mark the list filtered.
export function sharedFilterActive({ search, assignees, practices }: SharedFilterSources): boolean {
    return (
        search.trim() !== '' ||
        practices.ids.length > 0 ||
        appliedAssigneeLabels(assignees).length > 0
    );
}

// Line order: assignee, then the inbox's own lines (levels, tags), then practice and search.
export function buildListFilterSummary(
    shared: SharedFilterSources,
    inboxLines: readonly ListFilterLine[],
    total: number | null,
    status: DmInboxStatus,
    sortLine: string
): ListFilterSummary {
    const filters = [
        ...assigneeLine(shared.assignees),
        ...inboxLines,
        ...practiceLine(shared.practices),
        ...searchLine(shared.search)
    ];
    const filtered = inboxLines.length > 0 || sharedFilterActive(shared);
    return { leadText: listLeadText(total, filtered, status), filters, sortText: sortLine };
}
